import { ProjectFundingRequirement } from './ProjectFundingRequirement';
import type { ProjectFundingStatus } from '../types';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProjectFundingContract } from '../types';
import { BankGuidance } from './BankGuidance';

export type PaymentMethod = 'bank' | 'swish' | 'card';

/** The fees for one payment method, from GET /contract-config/project-economics. */
export interface PaymentMethodEconomics {
  projectValue: number;
  customerFee: number;
  paymentMethodFee: number;
  projectFee: number;
  minimumFeeAdjustment: number;
}

const METHODS: Array<{ method: PaymentMethod; icon: string; labelKey: string; subtitleKey?: string; timingKey: string }> = [
  { method: 'bank', icon: 'home', labelKey: 'projectFunding.picker.bank', subtitleKey: 'projectFunding.picker.bankVia', timingKey: 'projectFunding.picker.timingBank' },
  { method: 'card', icon: 'credit-card', labelKey: 'projectFunding.picker.card', subtitleKey: 'projectFunding.picker.cardSubtitle', timingKey: 'projectFunding.picker.timingInstant' },
  { method: 'swish', icon: 'smartphone', labelKey: 'projectFunding.picker.swish', timingKey: 'projectFunding.picker.timingInstant' }
];

/** The customer's fees split as on the invoice; the rows always add up to the total charged. */
export const paymentFeeRows = (economics: PaymentMethodEconomics) => {
  const minimumFee = Math.max(economics.minimumFeeAdjustment, 0);
  const projectFee = Math.max(economics.projectFee, 0);
  const transactionFee = Math.max(economics.customerFee - projectFee - minimumFee, 0);
  return {
    transactionFee,
    minimumFee,
    projectFee,
    total: economics.projectValue + economics.customerFee
  };
};

interface PaymentMethodPickerProps {
  /** Shows the bank guide for the chosen contract (ClickUp 869eu8fd9). */
  projectAccount?: boolean;
  depositAmount?: string;
  onDepositAmountChange?: (amount: string) => void;
  maximumAmountOre?: number;
  requirementAmountOre?: number;
  fundingAccount?: ProjectFundingStatus['account'];
  validAmount?: boolean;
  projectId?: string;
  projectNumber?: number | string;
  provider: 'adyen' | 'ping' | null;
  contracts: ProjectFundingContract[];
  contractId: string;
  onContractChange: (contractId: string) => void;
  /** The methods Admin has switched on and the environment allows (ClickUp 869fnhhhb). */
  enabledMethods: PaymentMethod[];
  economics: Partial<Record<PaymentMethod, PaymentMethodEconomics>>;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onClose: () => void;
  onProceed: (method: PaymentMethod) => void;
}

/**
 * "Insättning" – the customer's choice of payment method. Each method is a
 * large card with its total and specified fees; the card itself continues to
 * the payment.
 */
export function PaymentMethodPicker({
  projectAccount = false,
  depositAmount,
  onDepositAmountChange,
  maximumAmountOre = 0,
  requirementAmountOre = 0,
  fundingAccount,
  validAmount = true,
  projectId,
  projectNumber,
  provider,
  contracts,
  contractId,
  onContractChange,
  enabledMethods,
  economics,
  loading,
  error,
  onRetry,
  onClose,
  onProceed
}: PaymentMethodPickerProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'sv-SE';
  const money = (amount: number) => new Intl.NumberFormat(locale, {
    style: 'currency', currency: 'SEK', minimumFractionDigits: 0, maximumFractionDigits: 2
  }).format(amount);

  const methods = useMemo(
    () => METHODS.filter(({ method }) => enabledMethods.includes(method)),
    [enabledMethods]
  );
  const selectedContract = contracts.find(contract => contract.contractId === contractId) ?? (projectAccount ? contracts[0] ?? null : null);
  const loaded = provider === 'adyen' && !loading && !error && (!!selectedContract || projectAccount) && validAmount;
  const ready = loaded && methods.length > 0 && methods.every(({ method }) => economics[method]);
  // Admin has switched off every method: say so instead of an empty choice.
  const allOff = loaded && methods.length === 0;
  // One, two or three cards fill the row (the layout holds with a single method).
  const methodColumn = methods.length === 1 ? 'col-12' : methods.length === 2 ? 'col-12 col-md-6' : 'col-12 col-md-4';
  const lowestTotal = ready
    ? Math.min(...methods.map(({ method }) => paymentFeeRows(economics[method]!).total))
    : null;
  const amount = Object.values(economics).find(Boolean)?.projectValue ?? null;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="modal d-block payment-picker-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="project-payment-method-title"
      data-testid="project-payment-method-picker"
      onClick={onClose}
    >
      <div
        className="modal-dialog objection-dialog modal-lg modal-dialog-centered modal-dialog-scrollable modal-fullscreen-sm-down"
        onClick={event => event.stopPropagation()}
      >
        <div className="modal-content payment-picker">
          <div className="modal-header objection-header">
            <h2 className="modal-title payment-picker-title" id="project-payment-method-title">
              <i className="feather-credit-card me-2" aria-hidden="true" />
              {projectNumber != null
                ? t('projectFunding.picker.titleProject', { number: projectNumber })
                : t('projectFunding.picker.title')}
            </h2>
            <button type="button" className="btn-close" aria-label={t('projectFunding.closeAriaLabel')} onClick={onClose} />
          </div>

          <div className="modal-body">
            {!projectAccount && contracts.length > 1 && (
              <div className="mb-4">
                <label className="form-label payment-picker-label" htmlFor="project-payment-contract">
                  {t('projectFunding.contractToPayLabel')}
                </label>
                <select
                  id="project-payment-contract"
                  className="form-select form-select-lg"
                  value={contractId}
                  onChange={event => onContractChange(event.target.value)}
                >
                  <option value="" disabled>{t('projectFunding.selectContractToPay')}</option>
                  {contracts.map(contract => (
                    <option value={contract.contractId} key={contract.contractId}>
                      {t('projectFunding.contractOption', {
                        number: contract.contractNumber,
                        description: contract.contractName ?? '',
                        amount: money(contract.remainingFundingNeedOre / 100)
                      })}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {(selectedContract || projectAccount) && <div className="mb-4">
              <label className="form-label payment-picker-label" htmlFor="payment-picker-amount">
                {t('projectFunding.picker.amount')}
              </label>
              <div className="input-group input-group-lg">
                <input
                  id="payment-picker-amount"
                  type={projectAccount ? "number" : "text"}
                  inputMode="decimal"
                  min={projectAccount ? 0.01 : undefined}
                  step={projectAccount ? 0.01 : undefined}
                  max={projectAccount ? maximumAmountOre / 100 : undefined}
                  className="form-control payment-picker-amount"
                  value={projectAccount ? depositAmount : amount != null ? new Intl.NumberFormat(locale).format(amount) : ''}
                  onChange={projectAccount ? event => onDepositAmountChange?.(event.target.value) : undefined}
                  readOnly={!projectAccount}
                  aria-invalid={!validAmount}
                />
                <span className="input-group-text payment-picker-amount-unit">SEK</span>
              </div>
              {projectAccount && <>
                {fundingAccount && <div className="mt-2"><ProjectFundingRequirement account={fundingAccount} /></div>}
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {requirementAmountOre > 0 && <button type="button" className="btn btn-outline-primary payment-picker-amount-action" onClick={() => onDepositAmountChange?.(String(Math.min(requirementAmountOre, maximumAmountOre) / 100))}>{t('projectFunding.account.coverRequirement')}</button>}
                  <button type="button" className="btn btn-outline-primary payment-picker-amount-action" onClick={() => onDepositAmountChange?.(String(maximumAmountOre / 100))}>{t('projectFunding.account.allRemaining')}</button>
                </div>
                <p className="text-explain mt-2">{t('projectFunding.account.amountHint', { amount: money(maximumAmountOre / 100) })}</p>
                <p className="text-explain">{t('projectFunding.account.bankHint')}</p>
                {!validAmount && <p className="text-danger text-explain" role="alert">{t('projectFunding.account.invalidAmount', { amount: money(maximumAmountOre / 100) })}</p>}
              </>}
            </div>}

            {/* Before the methods: the cards themselves start the deposit. */}
            {selectedContract && projectId && (
              <BankGuidance projectId={projectId} contractId={selectedContract.contractId} amountOre={projectAccount && validAmount ? Math.round(Number(depositAmount) * 100) : undefined} />
            )}

            {loading && (
              <div className="alert alert-info d-flex align-items-center gap-2" role="status">
                <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                {t('projectFunding.loadingPaymentCosts')}
              </div>
            )}
            {error && (
              <div className="alert alert-danger" role="alert">
                <div>{error}</div>
                <button type="button" className="btn btn-outline-danger mt-2" onClick={onRetry}>
                  {t('projectFunding.tryAgain')}
                </button>
              </div>
            )}

            {allOff && (
              <div className="alert alert-info" role="status" data-testid="payment-picker-all-off">
                {t('projectFunding.picker.allOff')}
              </div>
            )}

            {projectAccount && provider === 'ping' && !loading && !error && <button
              type="button" className="btn btn-primary payment-picker-amount-action" disabled={!validAmount}
              onClick={() => onProceed('bank')}>{t('projectFunding.payIn')}</button>}

            {ready && (
              <section aria-labelledby="payment-picker-methods-title">
                <h3 className="payment-picker-section" id="payment-picker-methods-title">
                  {t('projectFunding.picker.methodsLegend')}
                </h3>
                <p className="payment-picker-hint mb-3" data-testid="payment-picker-contract-info">
                  {projectAccount ? t('projectFunding.account.freeAmount') : t('projectFunding.paymentContractInfo', { number: selectedContract!.contractNumber })}
                </p>
                <div className="row g-3">
                  {methods.map(entry => {
                    const methodEconomics = economics[entry.method]!;
                    const figures = paymentFeeRows(methodEconomics);
                    const name = entry.method === 'bank' && entry.subtitleKey
                      ? `${t(entry.labelKey)} ${t(entry.subtitleKey)}`
                      : t(entry.labelKey);
                    return (
                      <div className={methodColumn} key={entry.method}>
                        <button
                          type="button"
                          className={`payment-picker-option payment-picker-option-${entry.method} h-100 w-100 text-start`}
                          data-testid={`payment-method-${entry.method}`}
                          aria-label={t('projectFunding.picker.payWith', { method: name, total: money(figures.total) })}
                          onClick={() => onProceed(entry.method)}
                        >
                          <span className="d-flex align-items-start gap-2 mb-2">
                            <i className={`feather-${entry.icon} payment-picker-option-icon`} aria-hidden="true" />
                            <span className="flex-grow-1">
                              <span className="payment-picker-option-name d-block">{t(entry.labelKey)}</span>
                              {entry.subtitleKey && (
                                <span className="payment-picker-option-sub d-block">{t(entry.subtitleKey)}</span>
                              )}
                            </span>
                            <i className="feather-arrow-right payment-picker-option-go" aria-hidden="true" />
                          </span>
                          {figures.total === lowestTotal && (
                            <span className="badge payment-picker-badge mb-2">{t('projectFunding.picker.lowestCost')}</span>
                          )}
                          <span className="payment-picker-option-caption d-block">{t('projectFunding.picker.totalToPay')}</span>
                          <span className="payment-picker-option-total d-block">{money(figures.total)}</span>
                          <span className="payment-picker-option-caption d-block">{t(entry.timingKey)}</span>
                          <span className="payment-picker-option-fees d-block">
                            <span className="d-flex justify-content-between gap-2">
                              <span>{t('projectFunding.picker.amount')}</span>
                              <span>{money(methodEconomics.projectValue)}</span>
                            </span>
                            <span className="d-flex justify-content-between gap-2">
                              <span>{t('projectFunding.picker.transactionFeeShort')}</span>
                              <span>+{money(figures.transactionFee)}</span>
                            </span>
                            {figures.minimumFee > 0 && (
                              <span className="d-flex justify-content-between gap-2">
                                <span>{t('projectFunding.picker.minimumFee')}</span>
                                <span>+{money(figures.minimumFee)}</span>
                              </span>
                            )}
                            <span className="d-flex justify-content-between gap-2">
                              <span>{t('projectFunding.picker.projectFeeShort')}</span>
                              <span>+{money(figures.projectFee)}</span>
                            </span>
                          </span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
